import { AxiosError } from "axios";
import { t } from "i18next";
import { makeAutoObservable } from "mobx";
import { appStore } from "src/appStore";
import { apiAuthUrl, apiObjUrl } from "src/common/apiUrl";
import { makeDictionary } from "src/common/makeDictionary";
import { onError } from "src/common/onError";
import { RemoteData } from "src/common/RemoteData";
import { rest } from "src/common/rest";
import { restWithObjectVersion } from "src/common/restWithObjectVersion";
import {
  FormBlockDef,
  FormWithBlockStore,
} from "src/components/FormWithBlocks";
import { getIdLabels, IdLabel } from "src/references/getIdNames";
import { AttrTypeName, makeDictNameById } from "src/types/AttrType";
import { ZAttribute } from "src/types/ZAttribute";
import { zObjectItem, ZObjectItem } from "src/types/ZObjectItem";
import { ed2entity, EdCardValues } from "../EntityCardPage/apiEntityCard";
import { BuilderCtx } from "../EntityCardPage/blockBuilder/BuilderCtx";
import { buildMnenoGroup } from "../EntityCardPage/blockBuilder/buildMnemoGroup";
import { loadObjectAttrinbutesAll } from "../ManagementPage/objectsApi";
import { zPersonEntity, ZPersonEntity } from "../../types/ZPersonEntity";
import { EdPasswordChange } from "./PasswordChangeBlock/EdPasswordChange";
import { ZAttrLabelsData } from "../../common/attributes/makeAttrLabel/types/ZAttrLabelsData";
import { formatAllLabels } from "../../common/attributes/makeAttrLabel/utils/formatAllLabels";
import { getAllLabelDataAttributes } from "../../common/attributes/makeAttrLabel/utils/getAllLabelDataAttributes";

type PersonData = {
  roleObject: ZObjectItem;
  person: ZPersonEntity;
  attributes: ZAttribute[];
};

export class UserAccountPageStore {
  constructor() {
    makeAutoObservable(this);
  }

  labelsData: ZAttrLabelsData | null = null;

  setLabelsData(data: ZAttrLabelsData | null) {
    this.labelsData = data;
  }

  async init() {
    try {
      this.setData({ status: "wait" });
      if (this.attrTypesList.length === 0) {
        this.setAttrTypesList(await getIdLabels("attrType", "attrType"));
      }
      const { roleId: userRoleId, id: userId } = appStore.userInfo;

      // Договорились, что в запрос идёт roleId из /current.
      const resp = await restWithObjectVersion.get(
        apiObjUrl(`/roles/${userRoleId}`),
        { translate: true },
      );
      const roleObject = zObjectItem.parse(resp.data);

      const resp1 = await rest.get(apiObjUrl(`/users/${userId}`), {
        translate: true,
      });
      const person = zPersonEntity.parse(resp1.data);
      this.setLabelsData({
        labels: formatAllLabels(
          person.labelData?.labels || [],
          getAllLabelDataAttributes(person.labelData?.attributes),
          this.attrTypesDict,
        ),
        attributes: person.labelData?.attributes,
      });
      const attributes = await loadObjectAttrinbutesAll(roleObject.id, {
        translate: true,
      });

      this.setData({
        status: "ready",
        result: { roleObject, person, attributes },
      });
    } catch (error) {
      this.setData({ status: "error", error });
    }
  }

  changingPass = false;

  setChangingPass(value: boolean) {
    this.changingPass = value;
  }

  async changePassword(values: EdPasswordChange): Promise<boolean> {
    const { oldPass: currentPass, newPass } = values;
    try {
      this.setChangingPass(true);
      if (!currentPass) {
        throw Error("Old password not specified");
      }
      if (!newPass) {
        throw Error("New password not specified");
      }
      await rest.put(
        apiAuthUrl("/reset-password"),
        {
          currentPassword: currentPass,
          newPassword: newPass,
        },
        { translate: true },
      );
      return true;
    } catch (e) {
      // LPLM-1241 Костыль, т.к. на бэке не могут обработать эту ситуацию.
      const ex = e as AxiosError;
      if (ex.response?.status === 401) {
        onError(Error(t("Incorrect current password")));
      } else {
        onError(e);
      }
      return false;
    } finally {
      this.setChangingPass(false);
    }
  }

  async submitInfo(values: EdCardValues): Promise<boolean> {
    try {
      this.formStore.setSaving(true);
      if (this.data.status !== "ready") {
        throw Error("Data are not ready");
      }
      const { roleObject, person } = this.data.result;
      const entity = ed2entity(
        values,
        person.id,
        roleObject.id,
        this.attributesMap,
      );
      await rest.put(apiObjUrl(`/users/${person.id}`), entity);
      return true;
    } catch (e) {
      onError(e);
      return false;
    } finally {
      this.formStore.setSaving(false);
    }
  }

  attrTypesList: IdLabel[] = [];

  setAttrTypesList(list: IdLabel[]) {
    this.attrTypesList = list;
  }

  get attrTypesDict(): Record<number, string> {
    return makeDictNameById(this.attrTypesList);
  }

  data: RemoteData<PersonData> = { status: "none" };

  setData(newData: RemoteData<PersonData>) {
    this.data = newData;
  }

  formStore = new FormWithBlockStore();

  get userRootBlock(): FormBlockDef | null {
    const { data } = this;
    if (data.status !== "ready") return null;
    const { roleObject } = data.result;
    const ctx: BuilderCtx = {
      typesMap: this.attrTypesDict,
      labelsData: this.labelsData ?? undefined,
      // Внимание, костыль! Договорились, что открытым для редактирование полем будет атрибут с типом contentLanguage
      canUpdate: (attr, typesMap) =>
        typesMap[attr.valueType] === AttrTypeName.contentLanguage,
      objectId: String(roleObject.id),
    };
    return buildMnenoGroup("root", " ", roleObject.attributes, null, ctx);
  }

  get attributesMap(): Record<number, ZAttribute> {
    if (this.data.status !== "ready") return {};
    return makeDictionary(this.data.result.attributes, ({ id }) => id);
  }

  get initialData() {
    const result: Record<string, string[] | null> = {};
    if (this.data.status === "ready") {
      const { attributeValues } = this.data.result.person;
      attributeValues.forEach(({ attributeId, values }) => {
        result[String(attributeId)] = values;
      });
    }
    return result;
  }
}

export const userAccountPageStore = new UserAccountPageStore();
